import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import memberService from '../../services/memberService';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Divider,
  Tooltip,
  IconButton,
  Link,
  Container
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

const BulkMemberUploadPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState(null);
  const [showPasswords, setShowPasswords] = useState(false);
  const [includePasswords, setIncludePasswords] = useState(true);

  // Steps for the upload process
  const steps = ['Select CSV File', 'Upload & Process', 'Results'];

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type !== 'text/csv') {
      setError('Please upload a CSV file');
      setSelectedFile(null);
    } else {
      setError(null);
      setSelectedFile(file);
    }
  };

  // Generate CSV template for download
  const generateCsvTemplate = () => {
    const headers = "firstName,lastName,email,password,membershipType,status,joinDate,tags\n";
    const sampleRow = "John,Doe,john.doe@example.com,password123,regular,active,2023-01-01,\"tag1,tag2\"\n";
    const blob = new Blob([headers, sampleRow], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'member-upload-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('tenantId', localStorage.getItem('tenantId'));
      formData.append('includePasswords', includePasswords.toString());

      const response = await memberService.bulkUploadMembers(formData);
      
      if (response.success) {
        setUploadResults(response.data);
        setActiveStep(2); // Move to results step
      } else {
        setError(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading members:', error);
      setError(error.response?.data?.message || error.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Render file selection step
  const renderFileSelection = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Upload a CSV file with member data
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The CSV file should contain columns for firstName, lastName, email, and optionally password, membershipType, and status.
      </Typography>
      
      <Box sx={{ 
        border: '2px dashed #ccc', 
        borderRadius: 2, 
        p: 3, 
        textAlign: 'center',
        mb: 3,
        bgcolor: 'background.paper',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'rgba(63, 81, 181, 0.04)',
        }
      }}>
        <input
          accept=".csv"
          type="file"
          id="csv-upload"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <label htmlFor="csv-upload">
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUploadIcon />}
            sx={{ mb: 2 }}
          >
            Select CSV File
          </Button>
        </label>
        
        {selectedFile ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" color="primary">
              Selected file: {selectedFile.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Size: {(selectedFile.size / 1024).toFixed(2)} KB
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Drag and drop a file here or click to select a file
          </Typography>
        )}
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Need a template?
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={generateCsvTemplate}
          sx={{ mr: 2 }}
        >
          Download Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          onClick={() => navigate('/admin/members')}
          startIcon={<ArrowBackIcon />}
        >
          Back to Members
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setActiveStep(1)}
          disabled={!selectedFile}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );

  // Render upload confirmation step
  const renderUploadConfirmation = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Upload and Process
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          You're about to upload the file: <strong>{selectedFile?.name}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This will create new member accounts for each row in the CSV file.
        </Typography>
      </Box>
      
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid rgba(0,0,0,0.12)' }}>
        <Typography variant="subtitle1" gutterBottom>
          Options
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={includePasswords}
              onChange={(e) => setIncludePasswords(e.target.checked)}
              color="primary"
            />
          }
          label="Include temporary passwords in results"
        />
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          onClick={() => setActiveStep(0)}
          startIcon={<ArrowBackIcon />}
          disabled={isUploading}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={isUploading}
          startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
        >
          {isUploading ? 'Uploading...' : 'Upload and Process'}
        </Button>
      </Box>
    </Box>
  );

  // Render results step
  const renderResults = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Upload Results
      </Typography>
      
      {uploadResults && (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Paper sx={{ p: 2, flexGrow: 1, display: 'flex', alignItems: 'center', bgcolor: 'success.light', color: 'white' }}>
              <CheckCircleIcon sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">{uploadResults.successCount}</Typography>
                <Typography variant="body2">Successfully Added</Typography>
              </Box>
            </Paper>
            
            <Paper sx={{ p: 2, flexGrow: 1, display: 'flex', alignItems: 'center', bgcolor: uploadResults.failedCount > 0 ? 'error.light' : 'text.disabled', color: 'white' }}>
              <ErrorIcon sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">{uploadResults.failedCount}</Typography>
                <Typography variant="body2">Failed</Typography>
              </Box>
            </Paper>
          </Box>
          
          {uploadResults.success && uploadResults.success.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1">
                  Successful Imports ({uploadResults.success.length})
                </Typography>
                
                {includePasswords && (
                  <Tooltip title={showPasswords ? "Hide Passwords" : "Show Passwords"}>
                    <IconButton 
                      size="small" 
                      onClick={() => setShowPasswords(!showPasswords)}
                      color={showPasswords ? "primary" : "default"}
                    >
                      {showPasswords ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Membership #</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      {includePasswords && (
                        <TableCell>Temporary Password</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadResults.success.map((member, index) => (
                      <TableRow key={index}>
                        <TableCell>{`${member.firstName} ${member.lastName}`}</TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>{member.membershipNumber}</TableCell>
                        <TableCell>{member.membershipType}</TableCell>
                        <TableCell>{member.status}</TableCell>
                        {includePasswords && (
                          <TableCell>
                            {showPasswords ? member.password : '••••••••••'}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          
          {uploadResults.failed && uploadResults.failed.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Failed Imports ({uploadResults.failed.length})
              </Typography>
              
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row Data</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadResults.failed.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                            {item.data.firstName} {item.data.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.data.email}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: 'error.main' }}>
                          {item.error}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          onClick={() => navigate('/admin/members')}
          startIcon={<ArrowBackIcon />}
        >
          Back to Members
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSelectedFile(null);
            setActiveStep(0);
            setUploadResults(null);
            setError(null);
          }}
        >
          Upload Another File
        </Button>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, md: 4 }, mb: 4 }}>
      <Paper 
        elevation={0}
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2,
          background: 'linear-gradient(45deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <CloudUploadIcon sx={{ mr: 1 }} /> Bulk Member Upload
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8, mt: 1 }}>
          Upload multiple members at once using a CSV file
        </Typography>
      </Paper>

      <Paper sx={{ mb: 4, borderRadius: 2 }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        
        <Divider />
        
        {activeStep === 0 && renderFileSelection()}
        {activeStep === 1 && renderUploadConfirmation()}
        {activeStep === 2 && renderResults()}
      </Paper>
    </Container>
  );
};

export default BulkMemberUploadPage;